// Inside Index.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useLocation, useParams, NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getBlogs } from "../../apiCalls/urls/urlFunction";
import { titleToUrl } from "../../shared/extra";
import PageBreadcrumb from "../../components/other/PageBreadcrumb";
import PageLoading from "../../PageLoading";
import DefaultBlogImage1 from "../../assets/card-image/blog-detail.jpg";

const Index = ({ isMobile = false }) => {
  const [blogData, setBlogData] = useState([]);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  const { title } = useParams();
  const location = useLocation();
  const canonicalUrl = useMemo(
    () => `https://www.99gift.in${location.pathname}`,
    [location.pathname]
  );

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        const blogResponse = await getBlogs();
        const blogs = blogResponse?.data?.data || [];
        setBlogData(blogs);
        const matchedBlog = blogs.find(
          (blog) => titleToUrl(blog.title) === title
        );
        setCurrentBlog(matchedBlog);
      } catch (error) {
        console.error("An error occurred while loading the blog data.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, [title]);

  if (loading) return <PageLoading />;

const recentPosts = blogData
  ?.slice()
  ?.sort((a, b) => b.id - a.id) 
  ?.slice(0, 10); 
  
  return (
    <React.Fragment>
      <Helmet>
        <title>{currentBlog?.metaSEO?.metaTitle || currentBlog?.title}</title>
        <meta
          property="og:title"
          content={currentBlog?.metaSEO?.metaTitle || currentBlog?.title}
        />
        <meta
          name="description"
          content={currentBlog?.metaSEO?.metaDescription || currentBlog?.title}
        />
        <meta
          name="og:description"
          content={
            currentBlog?.metaSEO?.metaOGDescription || currentBlog?.title
          }
        />
        <meta property="og:url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />
        {currentBlog?.metaSchema && (
          <script type="application/ld+json">{currentBlog.metaSchema}</script>
        )}
      </Helmet>

      <Container maxWidth="xl">
        {!isMobile && (
          <PageBreadcrumb
            home="Home"
            other="Blog"
            currentProduct={currentBlog?.title}
          />
        )}
      </Container>

      <Container
        maxWidth="xl"
        sx={{ backgroundColor: "white.main", mb: { xs: 10 } }}
        disableGutters
      >
        <Grid container>
          <Grid item xs={12} md={8} lg={9} mt={3}>
            <Box sx={{ px: { xs: 1, sm: 1, lg: 2 } }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Box
                  component="img"
                  src={currentBlog?.blogBannerImage || DefaultBlogImage1}
                  alt={currentBlog?.title || "Blog Image"}
                  loading="lazy"
                  sx={{
                    width: "auto",
                    maxWidth: "100%",
                    height: { xs: "180px", md: "270px", lg: "300px" },
                    objectFit: "contain",
                    borderRadius: { xs: 2, md: 3 },
                    boxShadow: { xs: 0, md: 3 },
                  }}
                />
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "1.3rem", lg: "2rem" },
                  fontWeight: 400,
                  color: "primary.main",
                  mt: 2,
                }}
              >
                {currentBlog?.title}
              </Typography>

              <Box
                dangerouslySetInnerHTML={{ __html: currentBlog?.content || "" }}
                sx={{
                  mt: 1,
                  fontSize: { xs: "13.5px", lg: "15.5px" },
                  lineHeight: { xs: "16px", lg: "24.5px" },
                  color: "dark.main",
                  fontWeight: 400,
                  wordWrap: "break-word",
                  overflowX: "auto",
                  maxWidth: "100%",
                }}
              />
            </Box>
          </Grid>

          {/* Recent Blogs  */}
          <Grid
            item
            xs={12}
            md={4}
            lg={3}
            sx={{
              mt: { xs: 3, lg: 1 },
              pr: { xs: 1.5, sm: 2, md: 3 },
            }}
          >
            <Box sx={{ p: 2, border: "1px solid #ccc", borderRadius: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  textTransform: "uppercase",
                }}
              >
                Recent Blogs
              </Typography>

              <List dense>
                {recentPosts?.map((post) => (
                  <ListItem
                    key={post.id}
                    component={NavLink}
                    to={`/blog-details/${titleToUrl(post.title)}`}
                    button
                    sx={{
                      textDecoration: "none",
                      color: "#00B8EE",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        color: "black",
                        "& .MuiListItemText-primary": {
                          color: "black",
                        },
                      },
                    }}
                  >
                    <ListItemText
                      primary={`• ${post.title}`}
                      primaryTypographyProps={{ fontSize: "14px" }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </React.Fragment>
  );
};

export default Index;
